import React, { useState, useEffect, useContext } from "react";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import { RouterParams } from "../router/RouterParams";
import AddIcon from "@material-ui/icons/Add";
import { Grid, IconButton, Divider, Button, Box, DialogTitle, Typography, DialogContent, Dialog, DialogActions } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { ErrorSnackbar, SuccessSnackbar } from "../components/Snackbars";
import Gridable from "../components/Gridable";

import { ProviderFieldsRequest, ProviderFieldsResponse, TenantField, TenantSegment } from "../model/TenantFieldCatalog";
import { providerFields, updatePriority, deleteField, addFieldConfiguration } from "../api/TenantProviderFieldAPI";
import Ellipsis from "../components/Ellipsis";
import DialogPopup from "../components/DialogPopup";
import ProviderFieldMenu from "./ProviderFieldMenu";
import ValidatedInput from "../components/ValidatedInput";
import { GTZeroValidator } from "../components/Validators";
import ExpedientTemplateAddFieldsPopup from "../expedient_template/ExpedientTemplateAddFieldsPopup";

export default function ProviderFieldsList({ match }: RouterParams) {
    const context = useContext(AppContext);
    const tenantId = context.session?.tenant?.id || "-";

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<ProviderFieldsResponse>();

    const [field, setField] = useState<TenantField>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [popup, setPopup] = useState<"delete" | "edit" | "add">();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const gTZeroValidator = new GTZeroValidator();
    const [isInvalidPripority, setInvalidPripority] = useState<boolean>(true);

    const load = () => {
        providerFields(tenantId).then((response) => {
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    useEffect(load, [tenantId]);

    const onClickedOptions = (field: TenantField) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setField({ ...field });
    };

    const onClosePopup = () => {
        onCloseOption();
        setPopup(undefined);
    };

    const onDelete = () => {
        if (!field) return;

        setAnchorEl(null);
        setPopup("delete");
    };

    const onEdit = () => {
        if (!field) return;

        setAnchorEl(null);
        setPopup("edit");
    };

    const onAddField = () => {
        setAnchorEl(null);
        setPopup("add");
    };

    const onConfirmDelete = () => {
        if (!field) return;

        setSubmitting(true);
        deleteField(tenantId, field.id).then(() => {
            setSuccess(
                translate("provider_fields.delete.success", { name: field.name }) as string
            );
            onClosePopup();
            load();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onUpdatePriority = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!field) return;

        if (field!.provider_field!.priority <= 0) {
            return;
        }

        setSubmitting(true);
        updatePriority(tenantId, { id: field.id, priority: field!.provider_field!.priority }).then(() => {
            setSuccess(
                translate("provider_fields.edit.success", { name: field.name, priority: field.provider_field?.priority }) as string
            );
            onClosePopup();
            load();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onAddConfiguration = (field: (TenantField | TenantSegment)[]) => {
        setSubmitting(true);
        addFieldConfiguration(tenantId, { field_ids: field.map(e => e.id) } as ProviderFieldsRequest).then(() => {
            onClosePopup();
            load();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onClosedSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };


    const onCloseOption = () => {
        setAnchorEl(null);
        setField(undefined);
    };

    const onClickRow = (field: TenantField) => {
        setField({ ...field });
        setPopup("edit");
    };

    return (
        <Box >

            <Grid container alignItems="center" justifyContent="flex-end" className="TableFilter" spacing={1}>
                <Grid item>
                    <Button disabled={status === "loading"} aria-label="add" variant="text" color="primary" size="small" startIcon={<AddIcon />} onClick={onAddField}>
                        {translate("provider_fields.add_field")}
                    </Button>
                </Grid>
            </Grid>

            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("provider_fields.empty") as string}
                columns={[
                    {
                        title: translate("provider_fields.columns.priority") as string,
                        converter: (item) =>
                            <Button color="default" size="small" onClick={() => onClickRow(item)}>
                                {item!.provider_field!.priority}
                            </Button>,
                        lg: 1,
                        xl: 1
                    },
                    {
                        title: translate("provider_fields.columns.external_id") as string,
                        converter: (item) => (
                            <div style={{ overflowWrap: "break-word" }} >{item.external_id}</div>
                        ),
                        xs: false,
                        sm: false,
                        lg: 3,
                        xl: 3
                    },
                    {
                        title: translate("provider_fields.columns.field_name") as string,
                        converter: (item) => (
                            <Ellipsis text={item.name} lenght={100} uppercased={false} />
                        ),
                        xs: 2,
                        sm: 2,
                        lg: 2,
                        xl: 2
                    },
                    {
                        title: translate("provider_fields.columns.type") as string,
                        converter: (item) => (
                            <Ellipsis text={translate(`fields.type.${item.type}`) as string} uppercased={false} />
                        ),
                        xs: false,
                        sm: false,
                        lg: 2,
                        xl: 2
                    },
                    {
                        title: translate("provider_fields.columns.description") as string,
                        converter: (item) => (
                            <Ellipsis text={item.description ?? ""} lenght={100} uppercased={false} breakWord={false} />
                        ),
                        xs: true
                    },
                    {
                        title: (
                            <IconButton size="small" style={{ "visibility": "hidden" }} disabled>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        converter: (webhooks) => (
                            <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(webhooks)}>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        xs: "auto"
                    }
                ]} />
            {field && anchorEl &&
                <ProviderFieldMenu field={field} anchor={anchorEl} onDelete={onDelete} onEdit={onEdit} onClose={onCloseOption} />
            }
            {"add" === popup && (
                <ExpedientTemplateAddFieldsPopup
                    onClose={onClosePopup}
                    tenantId={tenantId}
                    typeField="provider_fields"
                    onSave={onAddConfiguration} />
            )}
            {field && "delete" === popup && (
                <DialogPopup
                    open
                    title={translate("provider_fields.delete.title")}
                    onClose={onClosePopup}
                    closeColor="default"
                    disableEscapeKeyDown={submitting}
                    disableBackdropClick={submitting}
                    disable={submitting}
                    button={
                        <Button
                            onClick={onConfirmDelete}
                            variant="outlined"
                            color="secondary"
                            disabled={submitting}
                        >
                            {translate("buttons.delete")}
                        </Button>
                    }
                >
                    {translate("provider_fields.delete.text", { name: field.name })}
                </DialogPopup>
            )}
            {field && "edit" === popup && (
                <Dialog open fullWidth={true}
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    className="DialogForm">
                    <form autoComplete="off" noValidate onSubmit={onUpdatePriority}>
                        <DialogTitle id="alert-dialog-title" disableTypography>
                            <Typography variant="subtitle1">
                                {translate("provider_fields.edit.title", {name:field.name}) as string}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item xs={12}>
                                    <ValidatedInput type="number" id="priority" name="priority"
                                        value={field!.provider_field!.priority + ""}
                                        label={translate("provider_fields.edit.label") as string}
                                        required={true} disabled={submitting}
                                        margin={"normal"}
                                        validators={[gTZeroValidator]}
                                        onValueChanged={(name: string, value: string) => {
                                            setField({
                                                ...field,
                                                provider_field: {
                                                    ...field.provider_field,
                                                    priority: Number(value ?? "1"),
                                                    active: field!.provider_field!.active
                                                }
                                            });
                                        }} />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClosePopup} variant="text" color="primary" disabled={submitting}>
                                {translate("buttons.cancel")}
                            </Button>
                            <Button type="submit" variant="contained" color="primary" size="medium" disabled={submitting} >
                                {translate("buttons.save")}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>
            )}
            <SuccessSnackbar message={success} onClose={onClosedSnackbars} />
            <ErrorSnackbar message={error} onClose={onClosedSnackbars} />
        </Box>
    );
}